import { getDesignPresetInheritedAssignment, type DesignPreset, type DesignPresetProperty } from 'common/design-tokens';
import { getDesignPresetDisplayValue, getDesignPresetPlatformName } from './design-preset-display';

/** A shared field must represent every side, including authored identity and inherited dimensions. */
export function getPresetSpacingAssignment({
	preset,
	properties,
	...context
}: Omit<Parameters<typeof getDesignPresetDisplayValue>[0], 'property' | 'assignment'> & {
	preset: DesignPreset;
	properties: readonly DesignPresetProperty[];
}) {
	const sides = properties.map(property => {
		const assignment = preset.properties[property] ?? {};
		const assigned = context.platform === 'base' ? assignment.base : assignment.platforms?.[context.platform];
		const inherited =
			context.platform === 'base'
				? undefined
				: getDesignPresetInheritedAssignment(assignment, context.platform, context.mediaQuery);
		return {
			assigned,
			inherited,
			value: assigned ?? inherited?.value,
			display: getDesignPresetDisplayValue({ ...context, property, assignment }),
		};
	});
	const first = sides[0];
	const mixed = sides.some(side => side.value !== first?.value || side.display?.value !== first?.display?.value);
	const inherited = !mixed ? first?.inherited : undefined;
	const sameSource = sides.every(side => side.inherited?.source === inherited?.source);
	return {
		mixed,
		assigned: sides.find(side => side.assigned !== undefined)?.assigned,
		inherited: inherited
			? {
					value: inherited.value,
					source: sameSource
						? getDesignPresetPlatformName(
								inherited.source,
								context.mediaQuery.config[inherited.source]?.uiName
							)
						: 'multiple platforms',
				}
			: undefined,
		display: mixed ? undefined : first?.display,
	};
}
